import type React from "react";
import { ChatInput } from "@/features/chat/components/ChatInput";
import { ChatMessageList } from "@/features/chat/components/ChatMessageList";
import { ThreadTabsBar } from "@/features/chat/components/ThreadTabsBar";
import { useChatbotPanel } from "@/features/chat/hooks/useChatbotPanel";

interface ChatbotPanelProps {
	/** When set, thread list is scoped to this workspace (all graphs in it). */
	workspaceId?: string | null;
	selectedDocumentIds?: string[];
	selectedGraphId?: string | null;
	onGraphGenerated?: (graphId: string) => void;
}

const ChatbotPanel: React.FC<ChatbotPanelProps> = ({
	workspaceId,
	selectedDocumentIds = [],
	selectedGraphId,
	onGraphGenerated,
}) => {
	const {
		threads,
		selectedThreadId,
		displayMessages,
		jobStatus,
		input,
		setInput,
		handleSubmit,
		handleNewThread,
		handleDeleteThread,
		handleSelectThread,
		sendIsPending,
		scrollRef,
	} = useChatbotPanel({
		workspaceId,
		selectedDocumentIds,
		selectedGraphId,
		onGraphGenerated,
	});

	return (
		<div className="flex h-full flex-col">
			<ThreadTabsBar
				threads={threads}
				selectedThreadId={selectedThreadId}
				onSelectThread={handleSelectThread}
				onDeleteThread={handleDeleteThread}
				onNewThread={handleNewThread}
				disableNewThread={sendIsPending}
			/>

			{/* Chat area */}
			<div className="flex-1 flex flex-col min-h-0">
				{jobStatus?.status === "completed" && (
					<div className="px-4 py-1 text-xs text-subtle">
						Graph build completed. You can continue chatting with the updated graph.
					</div>
				)}
				<ChatMessageList
					messages={displayMessages}
					selectedThreadId={selectedThreadId}
					threadsCount={threads.length}
					sendIsPending={sendIsPending}
					scrollRef={scrollRef}
					threadCreatedAt={threads.find((t) => t.id === selectedThreadId)?.createdAt ?? null}
				/>
				<ChatInput
					value={input}
					onChange={setInput}
					onSubmit={handleSubmit}
					selectedThreadId={selectedThreadId}
					sendIsPending={sendIsPending}
					selectedDocumentIds={selectedDocumentIds}
				/>
			</div>
		</div>
	);
};

export default ChatbotPanel;
